import { DEFAULT_ELEMENT_STYLE } from "../canvas/canvasDefaults";
import { getArrowPoints, getElementFrame } from "../canvas/geometry/elementBounds";
import {
  ARROW_BINDING_GAP,
  resolveSceneArrowBindings,
} from "../canvas/geometry/arrowBinding";
import {
  CANVAS_ELEMENT_TYPES,
  createArrowElement,
  createCanvasElementId,
  createDiamondElement,
  createEllipseElement,
  createRectangleElement,
  createTextElement,
  isBoxElement,
} from "../canvas/nativeCanvasElements";
import { CANVAS_AI_MAX_ELEMENTS } from "./canvasAi.constants";
import { canvasAiPatchSchema } from "./canvasAi.schema";

const AI_ELEMENT_TYPES = new Set([
  CANVAS_ELEMENT_TYPES.ARROW,
  CANVAS_ELEMENT_TYPES.DIAMOND,
  CANVAS_ELEMENT_TYPES.ELLIPSE,
  CANVAS_ELEMENT_TYPES.RECTANGLE,
  CANVAS_ELEMENT_TYPES.TEXT,
]);

const SHAPE_FACTORIES = Object.freeze({
  [CANVAS_ELEMENT_TYPES.DIAMOND]: createDiamondElement,
  [CANVAS_ELEMENT_TYPES.ELLIPSE]: createEllipseElement,
  [CANVAS_ELEMENT_TYPES.RECTANGLE]: createRectangleElement,
});

export class CanvasAiPatchError extends Error {
  constructor(message) {
    super(message);
    this.name = "CanvasAiPatchError";
  }
}

const createStats = () => ({ added: 0, deleted: 0, updated: 0 });
const getElementById = (elements, id) =>
  elements.find((element) => element.id === id);

const assertManageableElement = (element) => {
  if (!element) throw new CanvasAiPatchError("Element does not exist");
  if (!AI_ELEMENT_TYPES.has(element.type)) {
    throw new CanvasAiPatchError("Images and freehand paths are read-only");
  }
};

const createShape = ({ element, id }) => {
  const createElement = SHAPE_FACTORIES[element.type];
  const shape = createElement({
    id,
    point: { x: element.x, y: element.y },
    style: { ...DEFAULT_ELEMENT_STYLE, ...element.style },
  });

  return {
    ...shape,
    height: element.height,
    label: element.label ?? "",
    width: element.width,
  };
};

const createText = ({ element, id }) => {
  const textElement = createTextElement({
    id,
    point: { x: element.x, y: element.y },
    style: { ...DEFAULT_ELEMENT_STYLE, ...element.style },
  });

  return {
    ...textElement,
    style: { ...textElement.style, ...element.style },
    text: element.text,
  };
};

const resolveEndpoint = ({ elements, endpoint, references }) => {
  if (!endpoint.elementRef) {
    return { binding: undefined, point: { x: endpoint.x, y: endpoint.y } };
  }
  const targetId = references.get(endpoint.elementRef) ?? endpoint.elementRef;
  const target = getElementById(elements, targetId);
  if (!target || !isBoxElement(target) || target.type === CANVAS_ELEMENT_TYPES.IMAGE) {
    throw new CanvasAiPatchError(`Arrow target ${endpoint.elementRef} is not a shape`);
  }
  const frame = getElementFrame(target);
  return {
    binding: { elementId: targetId, gap: ARROW_BINDING_GAP },
    point: { x: frame.x + frame.width / 2, y: frame.y + frame.height / 2 },
  };
};

const buildBindings = ({ end, start }) =>
  start.binding || end.binding
    ? { end: end.binding, start: start.binding }
    : undefined;

const createArrow = ({ element, elements, id, references }) => {
  const start = resolveEndpoint({ elements, endpoint: element.start, references });
  const end = resolveEndpoint({ elements, endpoint: element.end, references });
  const arrow = createArrowElement({
    bindings: buildBindings({ end, start }),
    id,
    point: start.point,
    style: { ...DEFAULT_ELEMENT_STYLE, ...element.style },
  });

  return { ...arrow, x2: end.point.x, y2: end.point.y };
};

const createAddedElement = ({ element, elements, id, references }) => {
  if (SHAPE_FACTORIES[element.type]) return createShape({ element, id });
  if (element.type === CANVAS_ELEMENT_TYPES.TEXT) return createText({ element, id });
  return createArrow({ element, elements, id, references });
};

const applyAdd = ({ createId, elements, operation, references, stats }) => {
  if (references.has(operation.ref)) {
    throw new CanvasAiPatchError(`Duplicate temporary reference ${operation.ref}`);
  }
  const id = createId();
  const nextElement = createAddedElement({
    element: operation.element,
    elements,
    id,
    references,
  });
  references.set(operation.ref, id);
  stats.added += 1;
  return { changedId: id, elements: [...elements, nextElement] };
};

const applyBoxChanges = ({ changes, element }) => ({
  ...element,
  ...(changes.x === undefined ? {} : { x: changes.x }),
  ...(changes.y === undefined ? {} : { y: changes.y }),
  ...(changes.width === undefined ? {} : { width: changes.width }),
  ...(changes.height === undefined ? {} : { height: changes.height }),
  ...(changes.label === undefined ? {} : { label: changes.label }),
  ...(changes.style ? { style: { ...element.style, ...changes.style } } : {}),
});

const applyTextChanges = ({ changes, element }) => ({
  ...element,
  ...(changes.x === undefined ? {} : { x: changes.x }),
  ...(changes.y === undefined ? {} : { y: changes.y }),
  ...(changes.text === undefined ? {} : { text: changes.text }),
  ...(changes.style ? { style: { ...element.style, ...changes.style } } : {}),
});

const getExistingEndpoint = ({ binding, point }) => ({ binding, point });

const applyArrowChanges = ({ changes, element, elements, references }) => {
  const points = getArrowPoints(element);
  const start = changes.start
    ? resolveEndpoint({ elements, endpoint: changes.start, references })
    : getExistingEndpoint({
        binding: element.bindings?.start,
        point: { x: points.x1, y: points.y1 },
      });
  const end = changes.end
    ? resolveEndpoint({ elements, endpoint: changes.end, references })
    : getExistingEndpoint({
        binding: element.bindings?.end,
        point: { x: points.x2, y: points.y2 },
      });

  return {
    ...element,
    bindings: buildBindings({ end, start }),
    style: changes.style ? { ...element.style, ...changes.style } : element.style,
    x1: start.point.x,
    x2: end.point.x,
    y1: start.point.y,
    y2: end.point.y,
  };
};

const updateElement = ({ changes, element, elements, references }) => {
  if (isBoxElement(element)) return applyBoxChanges({ changes, element });
  if (element.type === CANVAS_ELEMENT_TYPES.TEXT) {
    return applyTextChanges({ changes, element });
  }
  return applyArrowChanges({ changes, element, elements, references });
};

const applyUpdate = ({ elements, operation, references, stats }) => {
  const element = getElementById(elements, operation.id);
  assertManageableElement(element);
  const updatedElement = updateElement({
    changes: operation.changes,
    element,
    elements,
    references,
  });
  stats.updated += 1;
  return {
    changedId: element.id,
    elements: elements.map((candidate) =>
      candidate.id === element.id ? updatedElement : candidate
    ),
  };
};

const applyDelete = ({ elements, operation, stats }) => {
  const element = getElementById(elements, operation.id);
  assertManageableElement(element);
  stats.deleted += 1;
  return {
    changedId: element.id,
    elements: elements.filter((candidate) => candidate.id !== element.id),
  };
};

const countManageableElements = (elements) =>
  elements.filter((element) => AI_ELEMENT_TYPES.has(element.type)).length;

const applyOperation = (params) => {
  if (params.operation.kind === "add") return applyAdd(params);
  if (params.operation.kind === "update") return applyUpdate(params);
  return applyDelete(params);
};

const parsePatch = (patch) => {
  const result = canvasAiPatchSchema.safeParse(patch);
  if (result.success) return result.data;
  throw new CanvasAiPatchError(result.error.issues[0]?.message ?? "Invalid canvas patch");
};

/**
 * Applies a validated model patch to an isolated element array.
 * @param {{ createId?: Function, elements: Array<object>, patch: object }} params
 * @returns {{ changedIds: Set<string>, elements: Array<object>, stats: object, summary: string }}
 */
export const applyCanvasAiPatch = ({
  createId = createCanvasElementId,
  elements,
  patch,
}) => {
  const validatedPatch = parsePatch(patch);
  const changedIds = new Set();
  const references = new Map();
  const stats = createStats();
  let workingElements = [...elements];

  validatedPatch.operations.forEach((operation) => {
    const result = applyOperation({
      createId,
      elements: workingElements,
      operation,
      references,
      stats,
    });
    workingElements = result.elements;
    changedIds.add(result.changedId);
    if (countManageableElements(workingElements) > CANVAS_AI_MAX_ELEMENTS) {
      throw new CanvasAiPatchError("The diagram exceeds the 200 element limit");
    }
  });

  return {
    changedIds,
    elements: resolveSceneArrowBindings(workingElements),
    stats,
    summary: validatedPatch.summary,
  };
};
